import { Component, Inject, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { environment } from '@env/environment';
import { PrincipalService } from '@core/auth/principal.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ThemeService } from '@app/core/services/theme.service';
import { MatDialog } from '@angular/material/dialog';
import { WelcomeDialogComponent } from '@app/shared/dialogs/welcome-dialog/welcome-dialog.component';

interface OktaUserClaims {
  name?: string;
  sub?: string;
  given_name?: string;
  login?: string;
  preferred_username?: string;
  [key: string]: unknown;
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: [],
    standalone: false
})
export class AppComponent implements OnInit {

  user: OktaUserClaims | null;
  idToken: string;
  accessToken: string;
  displayName: string;
  firstName: string;
  userName: string;
  userId: string;
  isAuthenticated: boolean;

  errorMessage: string;
  isLoading: boolean = true;
  private destroyRef = inject(DestroyRef);

  private static readonly WELCOME_DISMISSED_KEY = 'welcomeDialogDismissed.v2';

  constructor(
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    public authStateService: OktaAuthStateService,
    private principal: PrincipalService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private themeService: ThemeService,
    private dialog: MatDialog
  ) {
    this.themeService.initialize();
    this.authStateService.authState$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      (state) => this.isAuthenticated = state.isAuthenticated
    );
  }

  async ngOnInit() {
    this.activatedRoute.queryParams.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(params => {
      if ('storeNumber' in params) {
        localStorage.setItem('storeNumber', params['storeNumber']);
      } else {
        localStorage.setItem('storeNumber', '');
      }
    });

    this.isLoading = true;

    const isCallback = location.href.includes('/implicit/callback');

    // On the callback route, OktaCallbackComponent performs the token exchange
    // and then navigates to the original URI. We must NOT trigger login() here
    // (it would interrupt that flow), but we DO still need to react to the
    // resulting auth state below so the user/session loads and route guards can
    // resolve. So we skip the initial decision here and let the authState$
    // subscription handle it once tokens are stored.
    if (!isCallback) {
      // Start the Okta service so it hydrates tokens from storage (shared across
      // browser tabs) and emits an accurate auth state. Without this, authState$
      // emits a default "unauthenticated" value on subscribe, which bounces
      // already-signed-in users to the login screen. start() is idempotent.
      await this.oktaAuth.start();

      this.isAuthenticated = await this.oktaAuth.isAuthenticated();
      if (this.isAuthenticated) {
        this.getSessionDetails();
      } else {
        this.login();
      }
    }

    this.authStateService.authState$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(async (state) => {
      this.isAuthenticated = state.isAuthenticated;
      if (this.isAuthenticated) {
        this.getSessionDetails();
      } else if (!location.href.includes('/implicit/callback')) {
        this.login();
      }
    });
  }

  login() {
    try {
      const originalUri = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      this.oktaAuth.setOriginalUri(originalUri);
      this.oktaAuth.signInWithRedirect();
    }
    catch (error) {
      console.log(error);
    }
  }

  async getSessionDetails() {
    this.user = await this.oktaAuth.getUser();
    if (this.user) {
      this.displayName = this.user.name ?? '';
      this.userId = this.user.sub ?? '';
      this.firstName = this.user.given_name ?? '';
      this.userName = this.user.login?.substring(0, this.user.login.indexOf('@')) ?? this.user.preferred_username ?? '';
    }
    this.idToken = this.oktaAuth.getIdToken();
    this.accessToken = this.oktaAuth.getAccessToken();
    this.initializeUserInfo();
    this.routeToRoleHome();
    this.showWelcomeDialogOnce();
  }

  private showWelcomeDialogOnce() {
    if (localStorage.getItem(AppComponent.WELCOME_DISMISSED_KEY) === 'true') {
      return;
    }

    if (this.dialog.openDialogs.some(ref => ref.componentInstance instanceof WelcomeDialogComponent)) {
      return;
    }

    const dialogRef = this.dialog.open(WelcomeDialogComponent, {
      width: '480px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: false
    });

    dialogRef.afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      localStorage.setItem(AppComponent.WELCOME_DISMISSED_KEY, 'true');
    });
  }

  async logout() {
    const idToken = this.oktaAuth.getIdToken();

    try {
      await this.oktaAuth.signOut({ postLogoutRedirectUri: '/' });
    }
    catch {
      this.ngOnInit();
    }

    window.location.href = `${environment.oktaIssuerUrl}/v1/logout?id_token_hint=${idToken}&post_logout_redirect_uri=${environment.redirectUrl}`;
  }

  openHelpDialog() {
    if (this.dialog.openDialogs.some(ref => ref.componentInstance instanceof WelcomeDialogComponent)) {
      return;
    }

    this.dialog.open(WelcomeDialogComponent, {
      width: '480px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: false
    });
  }

  initializeUserInfo() {
    Promise.resolve(this.principal.getUser().toPromise().then((user) => {
    }));
  }

  routeToRoleHome() {
    const authorities: String[] = this.principal.getAuthorities();
    if (authorities.indexOf('CRADLEPOINT_PRO') !== -1 && authorities.indexOf('ADMIN') === -1 && authorities.indexOf('LOGIN') === -1) {
      this.router.navigate(['/automation/cradlepoint-provisioning']).then();
    }
  }

}